import React, { useState } from 'react'
import { Navigate } from 'react-router-dom'
import { Environment } from '../../../environment.js'
import { useGetAuthenticatedUser } from '../../../Flux/Selector/index.js'
import type { SpinExportFileKey } from '../../../Generated/SpinExportFile.ts'
import { SpinExportFiles } from '../../../Generated/SpinExportFile.js'
import { isGlobalAdmin } from '../../../Model/User/IUser.js'
import { Card, KpiTile } from '../AdminStatsPage/StatsUi.js'
import { SpinChrome } from './SpinChrome.js'
import { InfoTip } from './SpinUi.js'
import {
  EXPORT_FILE_KEYS,
  bundleContents,
  buildSpinExportUrl,
  estimatedSeconds,
  spinExportScope
} from './spinExport.logic.js'
import { tsLabel } from './spinMetrics.logic.js'
import { useSelectedRun } from './useSelectedRun.js'
import { useSpinMetrics } from './useSpinData.js'

/**
 * Raw SPIN data out of the database, as a ZIP of CSV/FASTA files — for work that has to happen outside
 * the product: re-BLASTing the representative sequences against a reference database to measure
 * precision/recall, or re-clustering the member sequences under different parameters.
 *
 * The page is deliberately thin. It picks a snapshot with the same three controls as every other SPIN
 * page (run/algorithm + taxonomy in the chrome above), lets the user choose which files they want, and
 * then hands off to a plain browser download — the full bundle measures ~430MB compressed on production
 * data, and the browser is far better at owning a transfer like that than an axios call rendering into
 * memory would be.
 *
 * What the page mostly does is set expectations, because the download's behaviour is unusual and would
 * otherwise read as a bug: it runs for about three minutes, the size is unknown up front (the response is
 * streamed as it is computed, so there is no Content-Length and no progress bar), and it cannot fail with
 * an error page once it has started — only truncate, which is why the format is a ZIP.
 */
export const SpinExportPage = () => {
  const user = useGetAuthenticatedUser()
  const sel = useSelectedRun()
  const { selected } = sel
  const { metrics } = useSpinMetrics(selected)

  const [files, setFiles] = useState<SpinExportFileKey[]>(EXPORT_FILE_KEYS)
  const scope = spinExportScope(selected)

  if (!user) return <></>
  if (!isGlobalAdmin(user)) return <Navigate to="/home" replace />

  const toggle = (key: SpinExportFileKey) =>
    setFiles((current) => (current.includes(key) ? current.filter((k) => k !== key) : [...current, key]))

  const snapshot = metrics?.snapshot
  const minutes = Math.max(1, Math.round(estimatedSeconds(files) / 60))
  const href = scope && files.length > 0 ? buildSpinExportUrl(Environment.API_URL, scope, files) : null

  return (
    <div className="space-y-8">
      <SpinChrome title="SPIN · Raw export" sel={sel} />

      <section className="space-y-3">
        <h2 className="text-xl font-semibold">The SPIN set as data</h2>
        <p className="text-sm text-gray-600 max-w-4xl">
          Downloads the <b>current SPIN set</b> of the selected run&apos;s algorithm, as of that run&apos;s timestamp, in
          plain CSV and FASTA. Everything in the bundle describes one snapshot resolved once, so the files join on{' '}
          <span className="font-mono">spin_id</span> without gaps, and the archive carries a{' '}
          <span className="font-mono">README.txt</span> documenting every column and both metrics — enough for someone
          outside the team to work from without asking.
        </p>
      </section>

      {snapshot && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KpiTile label="Algorithm" value={snapshot.algorithmType} />
          <KpiTile label="Taxonomy" value={snapshot.amplicon ?? 'all amplicons'} />
          <KpiTile
            label={
              <>
                SPINs in the snapshot
                <InfoTip tip="Current SPINs as of the selected run's timestamp — currency is by assigned_at (latest-wins per OTU), never by run id. This is the row count of spins.csv." />
              </>
            }
            value={snapshot.spinCount.toLocaleString()}
          />
          <KpiTile label="Member OTUs" value={snapshot.memberCount.toLocaleString()} />
        </div>
      )}

      {snapshot && (
        <p className="text-sm text-gray-500">
          Snapshot as of <b>{tsLabel(snapshot.asOf)}</b> — the selected run&apos;s start time. Any later reassignment is
          excluded, so the same run always yields the same bundle.
        </p>
      )}

      <Card title="Files to include">
        <div className="space-y-2">
          {EXPORT_FILE_KEYS.map((key) => {
            const file = SpinExportFiles[key]
            return (
              <label key={key} className="flex items-start gap-3 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  className="checkbox checkbox-sm mt-0.5"
                  checked={files.includes(key)}
                  onChange={() => toggle(key)}
                />
                <span>
                  <span className="font-mono">{file.fileName}</span> — {file.label}
                  <span className="text-gray-500"> ({file.costHint})</span>
                </span>
              </label>
            )
          })}
        </div>

        <p className="text-xs text-gray-500 mt-3">
          Sizes and times are measured on production-sized data (~435k SPINs over ~1.1M member OTUs) and are before
          compression — everything selected is 1.8 GB of files that arrive as a ~430 MB download. Deselect what you do
          not need; <span className="font-mono">spins.csv</span> is the slow one, because unity and reach have to score
          every member&apos;s taxonomy.
        </p>
      </Card>

      <section className="space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          {href ? (
            <a className="btn btn-primary btn-sm" href={href} download>
              Download bundle (~{minutes} min)
            </a>
          ) : (
            <button className="btn btn-primary btn-sm" disabled>
              Download bundle
            </button>
          )}
          <span className="text-sm text-gray-500">
            {!scope
              ? 'Select a run first.'
              : files.length === 0
                ? 'Select at least one file — otherwise the bundle is documentation only.'
                : `${bundleContents(files).length} entries: ${bundleContents(files).join(', ')}`}
          </span>
        </div>

        <div className="text-sm text-gray-600 max-w-4xl space-y-2">
          <p>
            <b>The download starts immediately and then keeps going for a while.</b> The bundle is built as it is sent,
            so your browser will show no size and no progress — that is expected, not a stall. Leave the tab open until
            it finishes.
          </p>
          <p>
            <b>If the archive does not open, the transfer was interrupted.</b> A ZIP is finalised by data written at the
            very end, which is exactly why this export is one: an incomplete download fails loudly instead of looking
            like a short but valid CSV. <span className="font-mono">MANIFEST.txt</span> is the last file written and
            lists the row count of every other one — if it is there, nothing was lost. Just request it again.
          </p>
          <p className="text-gray-500">
            Timestamps in the files are UTC and named <span className="font-mono">*_utc</span>, so a bundle does not
            depend on the server that produced it.
          </p>
        </div>
      </section>

      <Card title="What the recipients usually want">
        <div className="text-sm text-gray-600 space-y-2 max-w-4xl">
          <p>
            <b>To BLAST our SPINs.</b> <span className="font-mono">spins.fasta</span> alone is enough — one
            representative sequence per SPIN, identified by the bare SPIN id, so hits join straight back onto{' '}
            <span className="font-mono">spins.csv</span>. Add that CSV to compare against our own taxonomy call and
            cluster quality.
          </p>
          <p>
            <b>To re-cluster the same material.</b> <span className="font-mono">spin_members.fasta</span> holds every
            member sequence with its abundance in the usearch/vsearch{' '}
            <span className="font-mono">;size=</span> convention, so it feeds swarm or vsearch without preprocessing —
            which is what makes a “same data, different clustering parameters” comparison possible.
          </p>
        </div>
      </Card>
    </div>
  )
}
